import { Clock, AlertTriangle, CheckCircle2 } from 'lucide-react'

type NilGoStatus = 'not_started' | 'in_progress' | 'confirmed' | 'not_required'
type InstitutionalDisclosureStatus = 'not_started' | 'submitted' | 'acknowledged' | 'not_required'

interface Props {
  dealExecutedAt: string
  paymentAmount: number
  nilGoStatus: NilGoStatus
  institutionalStatus: InstitutionalDisclosureStatus
  school: { name: string } | null
}

function addBusinessDays(startDate: Date, days: number): Date {
  const result = new Date(startDate)
  let added = 0
  while (added < days) {
    result.setDate(result.getDate() + 1)
    const dayOfWeek = result.getDay()
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++
    }
  }
  return result
}

function getDaysRemaining(deadline: Date): number {
  const today = new Date()
  const diff = deadline.getTime() - today.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

function getCountdownColor(daysRemaining: number): string {
  if (daysRemaining > 3) return '#C0FD72'
  if (daysRemaining >= 1) return '#F9DA4C'
  return '#FFA07A'
}

function getCountdownText(daysRemaining: number): string {
  if (daysRemaining > 3) return `${daysRemaining} days remaining`
  if (daysRemaining === 1) return '1 day — act soon'
  if (daysRemaining > 0) return `${daysRemaining} days — act soon`
  return 'Overdue — submit immediately'
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function NilGoStatusBadge({ status }: { status: NilGoStatus }) {
  if (status === 'confirmed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#C0FD72]/15 border border-[#C0FD72]/30 text-[#C0FD72]">
        <CheckCircle2 className="w-3 h-3" />
        Confirmed
      </span>
    )
  }
  if (status === 'in_progress') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#F9DA4C]/15 border border-[#F9DA4C]/30 text-[#F9DA4C]">
        In Progress
      </span>
    )
  }
  if (status === 'not_required') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/10 border border-white/10 text-white/40">
        Below $600 Threshold
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/10 border border-white/20 text-white/60">
      Not Submitted
    </span>
  )
}

function InstitutionalStatusBadge({ status }: { status: InstitutionalDisclosureStatus }) {
  if (status === 'acknowledged') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#C0FD72]/15 border border-[#C0FD72]/30 text-[#C0FD72]">
        <CheckCircle2 className="w-3 h-3" />
        Acknowledged
      </span>
    )
  }
  if (status === 'submitted') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#C0FD72]/15 border border-[#C0FD72]/30 text-[#C0FD72]">
        <CheckCircle2 className="w-3 h-3" />
        Submitted
      </span>
    )
  }
  if (status === 'not_required') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/10 border border-white/10 text-white/40">
        Below $600 Threshold
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/10 border border-white/20 text-white/60">
      Not Started
    </span>
  )
}

const ComplianceDeadlineBanner = ({
  dealExecutedAt,
  nilGoStatus,
  institutionalStatus,
  school,
}: Props) => {
  const executedDate = new Date(dealExecutedAt)
  const nilGoDeadline = addBusinessDays(executedDate, 5)
  const institutionalDeadline = new Date(executedDate)
  institutionalDeadline.setDate(institutionalDeadline.getDate() + 30)

  const nilGoDays = getDaysRemaining(nilGoDeadline)
  const institutionalDays = getDaysRemaining(institutionalDeadline)

  const nilGoColor = getCountdownColor(nilGoDays)
  const institutionalColor = getCountdownColor(institutionalDays)

  const schoolName = school?.name ?? 'Your School'

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* NIL Go Card */}
        <div className="bg-white/10 border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-white/50 shrink-0 mt-0.5" />
              <span className="text-white text-sm font-semibold">CSC / NIL Go Reporting</span>
            </div>
            <NilGoStatusBadge status={nilGoStatus} />
          </div>

          <div>
            {nilGoStatus === 'not_required' ? (
              <p className="text-white/40 text-sm">No NIL Go submission required for this deal.</p>
            ) : (
              <>
                <p
                  className={`text-lg font-bold leading-tight ${nilGoDays <= 0 ? 'animate-pulse' : ''}`}
                  style={{ color: nilGoColor }}
                >
                  {getCountdownText(nilGoDays)}
                </p>
                <p className="text-white/50 text-xs mt-0.5">Due {formatDate(nilGoDeadline)}</p>
              </>
            )}
          </div>

          {nilGoStatus !== 'confirmed' && nilGoStatus !== 'not_required' && (
            <p className="text-white/40 text-xs">
              Penalty: immediate ineligibility until reported.
            </p>
          )}
        </div>

        {/* Institutional Disclosure Card */}
        <div className="bg-white/10 border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-white/50 shrink-0 mt-0.5" />
              <span className="text-white text-sm font-semibold">{schoolName} Disclosure</span>
            </div>
            <InstitutionalStatusBadge status={institutionalStatus} />
          </div>

          <div>
            {institutionalStatus === 'not_required' ? (
              <p className="text-white/40 text-sm">
                No institutional disclosure required for this deal.
              </p>
            ) : (
              <>
                <p
                  className={`text-lg font-bold leading-tight ${institutionalDays <= 0 ? 'animate-pulse' : ''}`}
                  style={{ color: institutionalColor }}
                >
                  {getCountdownText(institutionalDays)}
                </p>
                <p className="text-white/50 text-xs mt-0.5">
                  Due {formatDate(institutionalDeadline)}
                </p>
              </>
            )}
          </div>

          {institutionalStatus !== 'submitted' &&
            institutionalStatus !== 'acknowledged' &&
            institutionalStatus !== 'not_required' && (
              <p className="text-white/40 text-xs">
                Must include deal terms, compensation, and service provider.
              </p>
            )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2.5 bg-[#F9DA4C]/8 border border-[#F9DA4C]/20 rounded-xl px-4 py-3">
        <AlertTriangle className="w-4 h-4 text-[#F9DA4C] shrink-0 mt-0.5" />
        <p className="text-white/70 text-xs leading-relaxed">
          <span className="text-[#F9DA4C] font-semibold">NCAA rules are your responsibility.</span>{' '}
          Athios provides this information as a guide — it is not legal advice. Deals $600+ require
          submission to NIL Go within 5 business days of signing. Consult your athletic department
          compliance officer with questions.
        </p>
      </div>
    </div>
  )
}

export default ComplianceDeadlineBanner
